import React, { useId } from "react";
import RecipeToggle from "./RecipeToggle";

const optionLabel = (item) =>
  item.title || item.label || item.name || item.display || `#${item.id}`;

const isCheckedValue = (value) => {
  if (typeof value === "string") {
    return ["true", "1", "yes", "on"].includes(value.trim().toLowerCase());
  }
  return value === true || value === 1;
};

const RecipeResourceField = ({
  field,
  value,
  onChange,
  onFile,
  options,
  existing,
}) => {
  const generatedId = useId().replace(/:/g, "");
  const inputId = `recipe-resource-${field.key}-${generatedId}`;
  const normalizedOptions = (field.options || []).map((item) =>
    typeof item === "object" ? item : { value: item, label: item }
  );

  const fieldLabel = (
    <span className="recipe-field-label">
      {field.label} {field.required && <em>*</em>}
    </span>
  );

  if (field.type === "checkbox") {
    const checked = isCheckedValue(value);
    return (
      <div className="recipe-field">
        {fieldLabel}
        <RecipeToggle
          id={inputId}
          className="recipe-toggle-field"
          checked={checked}
          label={field.label}
          onChange={onChange}
        >
          <span className="recipe-toggle-status">{checked ? "On" : "Off"}</span>
        </RecipeToggle>
      </div>
    );
  }

  return (
    <div className="recipe-field">
      <label className="recipe-field-label" htmlFor={inputId}>
        {field.label} {field.required && <em>*</em>}
      </label>
      {field.type === "textarea" || field.type === "json" ? (
        <textarea
          id={inputId}
          className={field.type === "json" ? "recipe-json-input" : ""}
          rows={field.type === "json" ? 9 : undefined}
          value={value || ""}
          onChange={(event) => onChange(event.currentTarget.value)}
          placeholder={field.type === "json" ? "Valid JSON" : undefined}
        />
      ) : field.type === "select" ? (
        <select
          id={inputId}
          value={value ?? ""}
          onChange={(event) => onChange(event.currentTarget.value)}
        >
          <option value="">Select {field.label.toLowerCase()}</option>
          {normalizedOptions.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      ) : field.type === "resource" ? (
        <select
          id={inputId}
          value={value ?? ""}
          onChange={(event) => onChange(event.currentTarget.value)}
        >
          <option value="">
            {field.nullable ? "None" : `Select ${field.label.toLowerCase()}`}
          </option>
          {options.map((item) => (
            <option key={item.id} value={item.id}>
              {optionLabel(item)} (#{item.id})
            </option>
          ))}
        </select>
      ) : field.type === "multi-resource" ? (
        <select
          id={inputId}
          multiple
          className="recipe-multi-select"
          value={(value || []).map(String)}
          onChange={(event) =>
            onChange(
              [...event.currentTarget.selectedOptions].map((item) => item.value)
            )
          }
        >
          {options.map((item) => (
            <option key={item.id} value={item.id}>
              {optionLabel(item)}
            </option>
          ))}
        </select>
      ) : field.type === "file" ? (
        <div className="recipe-resource-file">
          {existing && <img src={existing} alt="Current" />}
          <input
            id={inputId}
            type="file"
            accept={field.accept}
            onChange={(event) => onFile(event.currentTarget.files?.[0] || null)}
          />
        </div>
      ) : (
        <input
          id={inputId}
          type={field.type || "text"}
          step={field.step}
          value={value ?? ""}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      )}
      {field.type === "multi-resource" && (
        <small>Hold Command or Ctrl to select more than one.</small>
      )}
    </div>
  );
};

export { isCheckedValue };
export default RecipeResourceField;
